import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { statusesList } from 'common/utils/statusHelpers';

import type { RootState } from '../../app/store';

type TenantUserMembershipSliceState = {
    processing: boolean;
    errorCode?: number;
    filters: {
        keywords: string[];
        dropdowns: {
            status: {
                keys: string[];
                asKeyword: boolean;
            };
            globalRoles: {
                keys: string[];
                asKeyword: boolean;
            };
        }
    },
};

const initialState: TenantUserMembershipSliceState = {
    processing: false,
    filters: {
        keywords: [],
        dropdowns: {
            status: {
                keys: [],
                asKeyword: false
            },
            globalRoles: {
                keys: [],
                asKeyword: true
            }
        },
    },
};

const tenantUserMembershipSlice = createSlice({
    name: 'tenantUserMembership',
    initialState,

    reducers: (create) => ({
        addNewFilter: create.reducer((state, action: PayloadAction<string>) => {
            if (!state.filters.keywords.includes(action.payload)) {
                state.filters.keywords.push(action.payload);
            }
        }),

        removeFilter: create.reducer((state, action: PayloadAction<string>) => {
            const index = state.filters.keywords.indexOf(action.payload);
            if (index > -1) {
                state.filters.keywords.splice(index, 1);
            }
        }),

        clearFilters: create.reducer(() => initialState),

        checkStatusFilter: create.reducer((state, action: PayloadAction<string>) => {
            const keys = state.filters.dropdowns.status.keys;
            const index = keys.indexOf(action.payload);
            if (index === -1) {
                keys.push(action.payload);
            } else {
                keys.splice(index, 1);
            }
        }),

        checkGlobalRoleFilter: create.reducer((state, action: PayloadAction<string>) => {
            const keys = state.filters.dropdowns.globalRoles.keys;
            const index = keys.indexOf(action.payload);
            if (index === -1) {
                keys.push(action.payload);
            } else {
                keys.splice(index, 1);
            }
        })
    })
});

// eslint-disable-next-line no-restricted-syntax
export default tenantUserMembershipSlice.reducer;
export const { addNewFilter, removeFilter, clearFilters, checkStatusFilter, checkGlobalRoleFilter } = tenantUserMembershipSlice.actions;
export const selectFilters = (state: RootState) => state.tenantUserMembership.filters;

// Memoized selectors to prevent unnecessary rerenders
const selectGlobalRoles = (state: RootState) => state.globalRoles.globalRoles;
const selectGlobalRoleKeys = (state: RootState) => state.tenantUserMembership.filters.dropdowns.globalRoles.keys;
const selectStatusKeys = (state: RootState) => state.tenantUserMembership.filters.dropdowns.status.keys;

export const selectFilteredRoles = createSelector(
    [selectGlobalRoles, selectGlobalRoleKeys],
    (globalRoles, globalRoleKeys) => globalRoles.filter(role => globalRoleKeys.includes(role.name))
);

export const selectFilteredStatus = createSelector(
    [selectStatusKeys],
    (statusKeys) => statusesList.filter(statusName => statusKeys.includes(statusName))
);
